/** Shared helpers for LP Workbench (line producer portal). */

import { countryNameFromIso2 } from './countries';

export { countryNameFromIso2 };

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function getLpName() {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email?.split('@')[0] || 'Producer';
  } catch {
    return 'Producer';
  }
}

export function formatLpDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatLpRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return formatLpDate(iso);
}

export function formatScheduleDate(iso) {
  if (!iso) return '-';
  const [y, m, d] = String(iso).split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

/** Long date without zero padding, as used across the Slate pages (e.g. 1 Mar 2026). */
export function formatLongDate(iso) {
  if (!iso) return '';
  const parts = String(iso).slice(0, 10).split('-');
  if (parts.length < 3) return '';
  const month = MONTHS_SHORT[parseInt(parts[1], 10) - 1];
  if (!month) return '';
  return `${parseInt(parts[2], 10)} ${month} ${parts[0]}`;
}

/** Task due label - production-details.html style (e.g. May 20). */
export function formatTaskDueLabel(iso) {
  if (!iso) return '';
  const parts = String(iso).split('-');
  if (parts.length < 3) return String(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = parseInt(parts[2], 10);
  return `${months[parseInt(parts[1], 10) - 1]} ${day}`;
}

export const INDUSTRY_META = {
  film_tv: { label: 'Film & TV', icon: 'ri-film-line', color: 'bg-violet-100 text-violet-700 border-violet-200' },
};

export function industryMeta(slug) {
  return INDUSTRY_META[slug] || {
    label: slug?.replace(/_/g, ' ') || 'Film & TV',
    icon: 'ri-film-line',
    color: 'bg-violet-100 text-violet-700 border-violet-200',
  };
}

export function participantStatusLabel(status) {
  if (status === 'registered') return 'Registered';
  return 'Invited';
}

export function participantStatusClass(status) {
  if (status === 'registered') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return 'bg-amber-50 text-amber-800 border-amber-200';
}

const CASE_STATUS_META = {
  not_started: { label: 'Not started', className: 'bg-slate-50 text-slate-600 border-slate-200' },
  new: { label: 'New case', className: 'bg-slate-50 text-slate-600 border-slate-200' },
  in_progress: { label: 'In progress', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  waiting_on_documents: { label: 'Needs documents', className: 'bg-amber-50 text-amber-800 border-amber-200' },
  waiting_on_client: { label: 'Waiting on client', className: 'bg-amber-50 text-amber-800 border-amber-200' },
  ready_to_submit: { label: 'Ready to submit', className: 'bg-violet-50 text-violet-700 border-violet-200' },
  submitted: { label: 'Submitted', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  in_review_govt: { label: 'Govt review', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  approved: { label: 'Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  completed: { label: 'Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  archived: { label: 'Archived', className: 'bg-slate-50 text-slate-500 border-slate-200' },
};

export function caseStatusLabel(status) {
  if (!status || status === 'not_started') return '-';
  return CASE_STATUS_META[status]?.label || status.replace(/_/g, ' ');
}

export function caseStatusClass(status) {
  if (!status || status === 'not_started') return 'bg-slate-50 text-slate-400 border-slate-100';
  return CASE_STATUS_META[status]?.className || 'bg-slate-50 text-slate-600 border-slate-200';
}

export function procedureDisplayLabel(project, procedureLabel) {
  if (project?.procedure_slug) return project.procedure_slug;
  if (procedureLabel) return procedureLabel;
  return 'Pending';
}

/** @deprecated Prefer CountryFlag component; emoji flags are unreliable on Windows. */
export function countryFlagEmoji(code) {
  const clean = String(code || '').trim().toUpperCase();
  if (clean.length !== 2) return '';
  return String.fromCodePoint(
    ...clean.split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

/**
 * @deprecated Prefer the CountryFlag component (inline SVG + lipis fallback).
 * Flag CDN PNG URLs are unreliable in some environments.
 */
export function countryFlagUrl(code, width = 24) {
  const clean = String(code || '').trim().toLowerCase();
  if (clean.length !== 2) return '';
  return `https://flagcdn.com/w${width}/${clean}.png`;
}

/** Display nationality from schedule value (ISO2 code or free-text label). */
export function formatParticipantNationality(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  if (raw.length === 2) return countryNameFromIso2(raw);
  return raw;
}

export function formatCountryList(codes) {
  if (!codes?.length) return '-';
  return codes.join(', ');
}

/** Destination label for Slate rows (shooting location + crew nationalities when available). */
export function formatDestinationLabel(production) {
  const shoot = (production?.destination_country || '').trim().toUpperCase();
  const crew = (production?.countries || []).filter((c) => c && c !== shoot);
  if (shoot && crew.length) return `${shoot} · crew ${crew.join(', ')}`;
  if (production?.countries?.length) return production.countries.join(', ');
  if (shoot) return shoot;
  return '-';
}

/**
 * Overall readiness for a production (Slate indicator).
 * Uses people count and invite/registration progress; detail view adds case-level signal.
 */
export function productionReadiness(production) {
  const people = production?.participant_count ?? 0;
  const registered = production?.registered_count ?? 0;
  const sent = production?.invites_sent_count ?? 0;

  if (production?.readiness_label && production?.readiness_percent != null) {
    return {
      label: production.readiness_label,
      percent: production.readiness_percent,
      tone: readinessTone(production.readiness_label),
    };
  }

  if (people === 0) {
    return { label: 'Not started', percent: 0, tone: 'slate' };
  }
  if (sent === 0) {
    return { label: 'Pending invites', percent: 8, tone: 'slate' };
  }
  const regRatio = registered / people;
  const sentRatio = sent / people;
  if (regRatio >= 0.85 && sentRatio >= 0.9) {
    return { label: 'On track', percent: Math.min(98, 72 + Math.round(regRatio * 26)), tone: 'emerald' };
  }
  if (regRatio >= 0.4) {
    return { label: 'In progress', percent: 40 + Math.round(regRatio * 45), tone: 'sky' };
  }
  if (sentRatio >= 0.5) {
    return { label: 'Invites out', percent: 18 + Math.round(regRatio * 35), tone: 'amber' };
  }
  return { label: 'Setting up', percent: 12 + Math.round(sentRatio * 20), tone: 'amber' };
}

function readinessTone(label) {
  const key = String(label || '').toLowerCase();
  if (key.includes('track') || key.includes('ready')) return 'emerald';
  if (key.includes('progress')) return 'sky';
  if (key.includes('invite') || key.includes('setting') || key.includes('pending')) return 'amber';
  return 'slate';
}

export function readinessBarClass(tone) {
  const map = {
    slate: 'bg-slate-300',
    amber: 'bg-amber-400',
    sky: 'bg-sky-500',
    emerald: 'bg-emerald-500',
  };
  return map[tone] || map.slate;
}

export function readinessBadgeClass(tone) {
  const map = {
    slate: 'bg-slate-50 text-slate-600 border-slate-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return map[tone] || map.slate;
}
